import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase/config';
import { collection, getDocs, addDoc, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Templates & Downloads for the Resources Hub. Everyone who can see the hub can
// download; TCF admins (isAdmin) get an upload box + delete controls.
export default function ResourceTemplates({ user, isAdmin }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      let docs = [];
      try {
        const snap = await getDocs(query(collection(db, 'resourceTemplates'), orderBy('order', 'asc')));
        docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch {
        const snap = await getDocs(collection(db, 'resourceTemplates'));
        docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
      // Respect the Hidden toggle from Manage Resources (older docs have no `active` field).
      setTemplates(docs.filter(d => d.active !== false));
    } catch {
      setTemplates([]);
    }
    setLoading(false);
  };

  const handleDownload = (t) => {
    const link = document.createElement('a');
    link.href = t.fileUrl;
    link.download = t.downloadName || 'template';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { setMsg({ type: 'error', text: 'Choose a file first.' }); return; }
    if (!title.trim()) { setMsg({ type: 'error', text: 'Give it a title first.' }); return; }
    setUploading(true);
    setMsg({ type: 'info', text: 'Uploading…' });
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `templates/${Date.now()}_${safeName}`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, file);
      const fileUrl = await getDownloadURL(sref);
      await addDoc(collection(db, 'resourceTemplates'), {
        title: title.trim(),
        description: description.trim(),
        fileUrl,
        storagePath: path,
        downloadName: safeName,
        fileType: (file.name.split('.').pop() || '').toUpperCase(),
        order: Date.now(),
        uploadedBy: user?.email || '',
        uploadedAt: serverTimestamp(),
      });
      setTitle('');
      setDescription('');
      setFile(null);
      setFileKey(k => k + 1);
      setMsg({ type: 'success', text: 'Added. It is now available to download.' });
      await load();
      setTimeout(() => setMsg(null), 4000);
    } catch (err) {
      setMsg({ type: 'error', text: 'Upload failed. ' + (err?.message || 'Try again.') });
    }
    setUploading(false);
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.title}"? This removes it for everyone.`)) return;
    try {
      if (t.storagePath) { try { await deleteObject(storageRef(storage, t.storagePath)); } catch { /* file gone */ } }
      await deleteDoc(doc(db, 'resourceTemplates', t.id));
      setTemplates(prev => prev.filter(x => x.id !== t.id));
    } catch (err) {
      setMsg({ type: 'error', text: 'Could not delete. ' + (err?.message || '') });
    }
  };

  if (loading) return <div style={s.loading}>Loading templates…</div>;

  return (
    <div>
      <div style={s.docHeader}>
        <div style={s.eyebrow}>Resource</div>
        <div style={s.docTitle}>Templates &amp; Downloads</div>
        <div style={s.docSub}>Ready-to-use templates and documents. Download, then adapt them for your organization.</div>
      </div>

      {isAdmin && (
        <form onSubmit={handleUpload} style={s.uploadCard}>
          <div style={s.uploadTitle}>Add a template</div>

          <label style={s.label}>File (Word, PDF, PowerPoint, or Excel)</label>
          <input
            key={fileKey}
            type="file"
            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,application/pdf"
            onChange={e => setFile(e.target.files[0] || null)}
            style={s.fileInput}
          />

          <label style={s.label}>Title</label>
          <input style={s.input} type="text" placeholder="AI Employee Policy Template" value={title} onChange={e => setTitle(e.target.value)} />

          <label style={s.label}>Description</label>
          <textarea style={{ ...s.input, minHeight: '60px', resize: 'vertical' }} placeholder="A sentence on what this is and how to use it." value={description} onChange={e => setDescription(e.target.value)} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '14px', flexWrap: 'wrap' }}>
            <button type="submit" style={s.addBtn} disabled={uploading}>{uploading ? 'Uploading…' : 'Add template'}</button>
            {msg && <div style={{ ...s.msgBox, ...(msg.type === 'error' ? s.msgError : msg.type === 'success' ? s.msgSuccess : s.msgInfo) }}>{msg.text}</div>}
          </div>
        </form>
      )}

      {templates.length === 0 ? (
        <div style={s.empty}>No templates yet.{isAdmin ? ' Add your first one above.' : ' Check back soon.'}</div>
      ) : (
        <div style={s.grid}>
          {templates.map(t => (
            <div key={t.id} style={s.card}>
              <div style={s.cardTop}>
                <span style={s.fileTag}>{t.fileType || 'FILE'}</span>
                <div style={s.cardTitle}>{t.title}</div>
                {t.description && <div style={s.cardDesc}>{t.description}</div>}
              </div>
              <div style={s.cardActions}>
                <button style={s.dlBtn} onClick={() => handleDownload(t)}>↓ Download</button>
                {isAdmin && <button style={s.delBtn} onClick={() => handleDelete(t)}>Delete</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const N = '#1B2A4A';
const s = {
  loading: { padding: '40px', textAlign: 'center', color: '#3D3D3D', fontFamily: 'DM Sans, sans-serif' },
  docHeader: { marginBottom: '20px' },
  eyebrow: { fontSize: '10px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  docTitle: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: N },
  docSub: { fontSize: '13px', color: '#8A8A8A', marginTop: '4px', fontWeight: '300' },
  uploadCard: { background: '#FFFFFF', border: '1.5px solid #D4A847', padding: '22px 24px', marginBottom: '28px' },
  uploadTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '700', color: N, marginBottom: '12px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', color: N, letterSpacing: '0.4px', textTransform: 'uppercase', margin: '12px 0 5px' },
  fileInput: { fontSize: '13px', fontFamily: 'DM Sans, sans-serif', color: '#3D3D3D' },
  input: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#fff', outline: 'none', boxSizing: 'border-box' },
  addBtn: { background: N, color: '#fff', border: 'none', padding: '11px 24px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  msgBox: { fontSize: '12px', padding: '8px 12px' },
  msgError: { background: '#fdecea', color: '#a32d2d' },
  msgSuccess: { background: '#eaf3de', color: '#3b6d11' },
  msgInfo: { background: '#eef3fb', color: '#185fa5' },
  empty: { padding: '24px', background: '#F2F0EC', color: '#3D3D3D', fontSize: '13px', fontFamily: 'DM Sans, sans-serif' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' },
  card: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', display: 'flex', flexDirection: 'column' },
  cardTop: { padding: '20px 22px', flex: 1 },
  fileTag: { display: 'inline-block', background: 'rgba(212,168,71,0.18)', color: '#8A6F2A', fontSize: '10px', fontWeight: '700', padding: '3px 9px', letterSpacing: '1px', marginBottom: '10px' },
  cardTitle: { fontFamily: 'Playfair Display, serif', fontSize: '17px', fontWeight: '700', color: N, marginBottom: '6px', lineHeight: '1.25' },
  cardDesc: { fontSize: '12.5px', color: '#3D3D3D', lineHeight: '1.6', fontWeight: '300' },
  cardActions: { display: 'flex', gap: '8px', padding: '14px 22px', borderTop: '1px solid #E8E6E2' },
  dlBtn: { background: N, color: '#fff', border: 'none', padding: '9px 18px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  delBtn: { background: '#fff', color: '#a32d2d', border: '1.5px solid #e6c4c4', padding: '9px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', marginLeft: 'auto' },
};
